import { useState, useEffect, useRef } from 'preact/hooks';
import { store } from '../store';
import { getLocale } from '../i18n';
import { getConfig } from '../config';
import { SubscribeModal } from './SubscribeModal';
import { subscribeEvents } from '../api';
import { getSessionId } from '../trackingPipeline';
import { canShow, recordShow, recordClose, recordSubmit } from '../subscribeGuards';
import { isTargetingMatch } from '../subscribeTargeting';

const EVENT_OPEN = 'widget:subscribe:open';

type TriggerType = 'manual' | 'time_on_site' | 'scroll_depth' | 'exit_intent';

function tryOpen(
  siteKey: string,
  settings: any,
  setOpen: (v: boolean) => void,
  setTriggerType: (t: TriggerType) => void
): boolean {
  const sub = settings?.subscribe;
  if (!sub?.enabled) return false;

  const ctx = {
    pathname: typeof window !== 'undefined' ? window.location.pathname || '/' : '/',
    isDesktop: typeof window !== 'undefined' && window.innerWidth >= 768,
    locale: getLocale(),
    utmSource: typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('utm_source') : null,
  };
  if (!isTargetingMatch(sub.targeting, ctx)) return false;
  if (!canShow(siteKey, { frequency_cap: sub.frequency_cap, cooldown_after_close_hours: sub.cooldown_after_close_hours, stop_after_submit: sub.stop_after_submit !== false })) return false;

  recordShow(siteKey);
  setTriggerType('manual');
  subscribeEvents(getConfig(), 'subscribe_shown', {
    trigger_type: 'manual',
    source_page: ctx.pathname,
    session_id: getSessionId(),
  });
  setOpen(true);
  return true;
}

export function SubscribeModalRoot() {
  const [open, setOpen] = useState(false);
  const [triggerType, setTriggerType] = useState<TriggerType>('manual');
  const [settings, setSettings] = useState(store.getSettings());
  const triggeredRef = useRef(false);
  const config = getConfig();

  useEffect(() => {
    return store.subscribe(() => setSettings(store.getSettings()));
  }, []);

  useEffect(() => {
    const onOpen = () => tryOpen(config.siteKey, settings, setOpen, setTriggerType);
    window.addEventListener(EVENT_OPEN, onOpen);
    return () => window.removeEventListener(EVENT_OPEN, onOpen);
  }, [config.siteKey, settings]);

  // Auto-triggers (modal only, inline = manual)
  useEffect(() => {
    const sub = settings?.subscribe;
    if (!sub?.enabled || sub.mode !== 'modal' || !config.siteKey) return;
    const ctx = {
      pathname: typeof window !== 'undefined' ? window.location.pathname || '/' : '/',
      isDesktop: typeof window !== 'undefined' && window.innerWidth >= 768,
      locale: getLocale(),
      utmSource: typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('utm_source') : null,
    };
    if (!isTargetingMatch(sub.targeting, ctx)) return;
    if (!canShow(config.siteKey, { frequency_cap: sub.frequency_cap, cooldown_after_close_hours: sub.cooldown_after_close_hours, stop_after_submit: sub.stop_after_submit !== false })) return;

    const trigger = sub.trigger?.type ?? 'manual';
    if (trigger === 'manual') return;
    if (triggeredRef.current) return;

    const doOpen = (t: TriggerType) => {
      if (triggeredRef.current) return;
      triggeredRef.current = true;
      recordShow(config.siteKey);
      subscribeEvents(getConfig(), 'subscribe_shown', {
        trigger_type: t,
        source_page: ctx.pathname,
        session_id: getSessionId(),
      });
      setTriggerType(t);
      setOpen(true);
    };

    let tm: number | undefined;
    if (trigger === 'time_on_site') {
      const delay = Math.max(5, sub.trigger?.delay_seconds ?? 20) * 1000;
      tm = window.setTimeout(() => doOpen('time_on_site'), delay);
    } else if (trigger === 'scroll_depth') {
      const target = Math.min(100, Math.max(10, sub.trigger?.scroll_percent ?? 50));
      const handler = () => {
        const max = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
        const pct = (window.scrollY / max) * 100;
        if (pct >= target) {
          window.removeEventListener('scroll', handler);
          doOpen('scroll_depth');
        }
      };
      window.addEventListener('scroll', handler);
      handler();
      return () => window.removeEventListener('scroll', handler);
    } else if (trigger === 'exit_intent' && ctx.isDesktop) {
      const handler = (e: MouseEvent) => {
        if (e.clientY <= 10) {
          document.removeEventListener('mouseout', handler);
          doOpen('exit_intent');
        }
      };
      document.addEventListener('mouseout', handler);
      return () => document.removeEventListener('mouseout', handler);
    }

    return () => { if (tm) clearTimeout(tm); };
  }, [config.siteKey, settings]);

  const handleClose = (didSubmit: boolean) => {
    if (didSubmit) recordSubmit(config.siteKey);
    else recordClose(config.siteKey);
    setOpen(false);
  };

  const sub = settings?.subscribe;
  if (!sub?.enabled) return null;
  const ctx = { pathname: typeof window !== 'undefined' ? window.location.pathname || '/' : '/', isDesktop: typeof window !== 'undefined' && window.innerWidth >= 768, locale: getLocale(), utmSource: typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('utm_source') : null };
  if (!isTargetingMatch(sub.targeting, ctx)) return null;

  return (
    <SubscribeModal
      open={open}
      onClose={(didSubmit) => handleClose(didSubmit)}
      triggerType={triggerType}
      settings={settings}
    />
  );
}
